import React, { useState } from 'react';
import classnames from 'classnames/bind';

import {
    useGetAllWorkspacesQuery,
    useCreateWorkspaceMutation,
    useUpdateWorkspaceMutation,
    useDeleteWorkspaceMutation,
    useDuplicateWorkspaceMutation,
} from '@SERVICES';

import BasicInput from '@COMPONENTS/COMMON/inputs/input/BasicInput';
import BasicTextarea from '@COMPONENTS/COMMON/inputs/textarea/BasicTextarea';
import BasicButton from '@COMPONENTS/COMMON/buttons/BasicButton';
import BasicCheckbox from '@COMPONENTS/COMMON/inputs/checkbox/BasicCheckbox';
import { notify } from '@NOTIFICATION/Notificator';

import styles from './WorkspaceManagementTab.module.scss';

const cx: CX = classnames.bind(styles);

function WorkspaceManagementTab() {
    const { data: workspaces = [], isLoading, refetch } = useGetAllWorkspacesQuery();
    const [createWorkspace, { isLoading: isCreating }] = useCreateWorkspaceMutation();
    const [updateWorkspace, { isLoading: isUpdating }] = useUpdateWorkspaceMutation();
    const [deleteWorkspace, { isLoading: isDeleting }] = useDeleteWorkspaceMutation();
    const [duplicateWorkspace, { isLoading: isDuplicating }] = useDuplicateWorkspaceMutation();

    const [showCreateForm, setShowCreateForm] = useState(false);
    const [editingWorkspace, setEditingWorkspace] = useState<number | null>(null);
    const [duplicatingWorkspace, setDuplicatingWorkspace] = useState<number | null>(null);

    const [createFormData, setCreateFormData] = useState({
        name: '',
        description: '',
    });

    const [editFormData, setEditFormData] = useState({
        name: '',
        description: '',
        isActive: true,
    });

    const [duplicateFormData, setDuplicateFormData] = useState({
        name: '',
        description: '',
        copyUsers: true,
        copyDocuments: true,
        copyAppSettings: true,
        copyDocumentSettings: true,
        copyBrands: true,
        copyRegions: true,
        copyAreas: true,
        copyCountries: true,
        copyProperties: true,
        copyTags: true,
        copyVendors: true,
    });

    const handleCreateSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await createWorkspace(createFormData).unwrap();
            notify.success('Workspace created successfully');
            setShowCreateForm(false);
            setCreateFormData({ name: '', description: '' });
            refetch();
        } catch (error) {
            notify.error('Failed to create workspace');
        }
    };

    const handleEditSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingWorkspace) return;
        
        try {
            await updateWorkspace({
                id: editingWorkspace,
                name: editFormData.name,
                description: editFormData.description,
                is_active: editFormData.isActive,
            }).unwrap();
            notify.success('Workspace updated successfully');
            setEditingWorkspace(null);
            refetch();
        } catch (error) {
            notify.error('Failed to update workspace');
        }
    };

    const handleDelete = async (workspaceId: number) => {
        if (!window.confirm('Are you sure you want to delete this workspace?')) {
            return;
        }
        
        try {
            await deleteWorkspace(workspaceId).unwrap();
            notify.success('Workspace deleted successfully');
            refetch();
        } catch (error) {
            notify.error('Failed to delete workspace');
        }
    };

    const handleDuplicate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!duplicatingWorkspace) return;
        
        try {
            await duplicateWorkspace({
                id: duplicatingWorkspace,
                params: {
                    name: duplicateFormData.name,
                    description: duplicateFormData.description,
                    copy_users: duplicateFormData.copyUsers,
                    copy_documents: duplicateFormData.copyDocuments,
                    copy_app_settings: duplicateFormData.copyAppSettings,
                    copy_document_settings: duplicateFormData.copyDocumentSettings,
                    copy_brands: duplicateFormData.copyBrands,
                    copy_regions: duplicateFormData.copyRegions,
                    copy_areas: duplicateFormData.copyAreas,
                    copy_countries: duplicateFormData.copyCountries,
                    copy_properties: duplicateFormData.copyProperties,
                    copy_tags: duplicateFormData.copyTags,
                    copy_vendors: duplicateFormData.copyVendors,
                },
            }).unwrap();
            notify.success('Workspace duplicated successfully');
            setDuplicatingWorkspace(null);
            setDuplicateFormData({
                name: '',
                description: '',
                copyUsers: true,
                copyDocuments: true,
                copyAppSettings: true,
                copyDocumentSettings: true,
                copyBrands: true,
                copyRegions: true,
                copyAreas: true,
                copyCountries: true,
                copyProperties: true,
                copyTags: true,
                copyVendors: true,
            });
            refetch();
        } catch (error) {
            notify.error('Failed to duplicate workspace');
        }
    };

    const startEdit = (workspace: any) => {
        setEditingWorkspace(workspace.id);
        setEditFormData({
            name: workspace.name,
            description: workspace.description,
            isActive: workspace.isActive,
        });
    };

    const startDuplicate = (workspace: any) => {
        setDuplicatingWorkspace(workspace.id);
        setDuplicateFormData({
            name: `${workspace.name} (Copy)`,
            description: workspace.description,
            copyUsers: true,
            copyDocuments: true,
            copyAppSettings: true,
            copyDocumentSettings: true,
            copyBrands: true,
            copyRegions: true,
            copyAreas: true,
            copyCountries: true,
            copyProperties: true,
            copyTags: true,
            copyVendors: true,
        });
    };

    if (isLoading) {
        return <div>Loading workspaces...</div>;
    }

    return (
        <div className={cx('workspace-management-tab')}>
            <div className={cx('form-section')}>
                <div className={cx('header')}>
                    <h3>Workspace Management</h3>
                    <BasicButton
                        title="Create New Workspace"
                        onClick={() => setShowCreateForm(true)}
                        isProcessing={showCreateForm}
                    />
                </div>

                {/* Create Workspace Form */}
                {showCreateForm && (
                    <div className={cx('form-section-inner')}>
                        <h4>Create New Workspace</h4>
                        <form onSubmit={handleCreateSubmit}>
                            <div className={cx('form-group')}>
                                <BasicInput
                                    label="Workspace Name"
                                    value={createFormData.name}
                                    onChange={(value: string) => setCreateFormData((prev) => ({ ...prev, name: value }))}
                                    placeholder="Enter workspace name"
                                />
                            </div>
                            <div className={cx('form-group')}>
                                <BasicTextarea
                                    title="Description"
                                    value={createFormData.description}
                                    onChange={(value: string) => setCreateFormData((prev) => ({ ...prev, description: value }))}
                                    placeholder="Enter workspace description"
                                />
                            </div>
                            <div className={cx('form-actions')}>
                                <BasicButton
                                    title={isCreating ? 'Creating...' : 'Create Workspace'}
                                    onClick={handleCreateSubmit}
                                    isProcessing={isCreating}
                                />
                                <BasicButton
                                    title="Cancel"
                                    onClick={() => setShowCreateForm(false)}
                                />
                            </div>
                        </form>
                    </div>
                )}

                {/* Workspaces List */}
                <div className={cx('workspaces-list')}>
                    {workspaces.map((workspace) => (
                        <div key={workspace.id} className={cx('workspace-item')}>
                            {editingWorkspace === workspace.id ? (
                                <form onSubmit={handleEditSubmit} className={cx('edit-form')}>
                                    <div className={cx('form-group')}>
                                        <BasicInput
                                            label="Name"
                                            value={editFormData.name}
                                            onChange={(value: string) => setEditFormData((prev) => ({ ...prev, name: value }))}
                                        />
                                    </div>
                                    <div className={cx('form-group')}>
                                        <BasicTextarea
                                            title="Description"
                                            value={editFormData.description}
                                            onChange={(value: string) => setEditFormData((prev) => ({ ...prev, description: value }))}
                                        />
                                    </div>
                                    <div className={cx('form-group')}>
                                        <BasicCheckbox
                                            title="Active"
                                            checked={editFormData.isActive}
                                            onChange={() => setEditFormData((prev) => ({ ...prev, isActive: !prev.isActive }))}
                                        />
                                    </div>
                                    <div className={cx('form-actions')}>
                                        <BasicButton
                                            title={isUpdating ? 'Saving...' : 'Save'}
                                            onClick={handleEditSubmit}
                                            isProcessing={isUpdating}
                                        />
                                        <BasicButton
                                            title="Cancel"
                                            onClick={() => setEditingWorkspace(null)}
                                        />
                                    </div>
                                </form>
                            ) : (
                                <div className={cx('workspace-info')}>
                                    <div className={cx('workspace-header')}>
                                        <h4>{workspace.name}</h4>
                                        <div className={cx('workspace-status')}>
                                            {workspace.isActive ? 'Active' : 'Inactive'}
                                        </div>
                                    </div>
                                    {workspace.description && (
                                        <p className={cx('workspace-description')}>{workspace.description}</p>
                                    )}
                                    <div className={cx('workspace-stats')}>
                                        <span>
                                            {workspace.userCount || 0}
                                            {' '}
                                            users
                                        </span>
                                        <span>
                                            {workspace.documentCount || 0}
                                            {' '}
                                            documents
                                        </span>
                                    </div>
                                    <div className={cx('workspace-actions')}>
                                        <BasicButton
                                            title="Edit"
                                            onClick={() => startEdit(workspace)}
                                        />
                                        <BasicButton
                                            title="Duplicate"
                                            onClick={() => startDuplicate(workspace)}
                                        />
                                        <BasicButton
                                            title="Delete"
                                            onClick={() => handleDelete(workspace.id)}
                                            isProcessing={isDeleting}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Duplicate Workspace Modal */}
                {duplicatingWorkspace && (
                    <div className={cx('modal-overlay')}>
                        <div className={cx('modal')}>
                            <h4>Duplicate Workspace</h4>
                            <form onSubmit={handleDuplicate}>
                                <div className={cx('form-group')}>
                                    <BasicInput
                                        label="New Workspace Name"
                                        value={duplicateFormData.name}
                                        onChange={(value: string) => setDuplicateFormData((prev) => ({ ...prev, name: value }))}
                                    />
                                </div>
                                <div className={cx('form-group')}>
                                    <BasicTextarea
                                        title="Description"
                                        value={duplicateFormData.description}
                                        onChange={(value: string) => setDuplicateFormData((prev) => ({ ...prev, description: value }))}
                                    />
                                </div>
                                <div className={cx('duplicate-options')}>
                                    <h5>What to copy:</h5>
                                    <div className={cx('checkbox-group')}>
                                        <BasicCheckbox
                                            title="Copy Users"
                                            checked={duplicateFormData.copyUsers}
                                            onChange={() => setDuplicateFormData((prev) => ({ ...prev, copyUsers: !prev.copyUsers }))}
                                        />
                                        <BasicCheckbox
                                            title="Copy Documents (chapters, sections, variants, etc.)"
                                            checked={duplicateFormData.copyDocuments}
                                            onChange={() => setDuplicateFormData((prev) => ({ ...prev, copyDocuments: !prev.copyDocuments }))}
                                        />
                                        <BasicCheckbox
                                            title="Copy App Settings"
                                            checked={duplicateFormData.copyAppSettings}
                                            onChange={() => setDuplicateFormData((prev) => ({ ...prev, copyAppSettings: !prev.copyAppSettings }))}
                                        />
                                        <BasicCheckbox
                                            title="Copy Document Settings"
                                            checked={duplicateFormData.copyDocumentSettings}
                                            onChange={() => setDuplicateFormData((prev) => ({ ...prev, copyDocumentSettings: !prev.copyDocumentSettings }))}
                                        />
                                        <BasicCheckbox
                                            title="Copy Brands"
                                            checked={duplicateFormData.copyBrands}
                                            onChange={() => setDuplicateFormData((prev) => ({ ...prev, copyBrands: !prev.copyBrands }))}
                                        />
                                        <BasicCheckbox
                                            title="Copy Regions"
                                            checked={duplicateFormData.copyRegions}
                                            onChange={() => setDuplicateFormData((prev) => ({ ...prev, copyRegions: !prev.copyRegions }))}
                                        />
                                        <BasicCheckbox
                                            title="Copy Areas"
                                            checked={duplicateFormData.copyAreas}
                                            onChange={() => setDuplicateFormData((prev) => ({ ...prev, copyAreas: !prev.copyAreas }))}
                                        />
                                        <BasicCheckbox
                                            title="Copy Countries"
                                            checked={duplicateFormData.copyCountries}
                                            onChange={() => setDuplicateFormData((prev) => ({ ...prev, copyCountries: !prev.copyCountries }))}
                                        />
                                        <BasicCheckbox
                                            title="Copy Property Types"
                                            checked={duplicateFormData.copyProperties}
                                            onChange={() => setDuplicateFormData((prev) => ({ ...prev, copyProperties: !prev.copyProperties }))}
                                        />
                                        <BasicCheckbox
                                            title="Copy Tags"
                                            checked={duplicateFormData.copyTags}
                                            onChange={() => setDuplicateFormData((prev) => ({ ...prev, copyTags: !prev.copyTags }))}
                                        />
                                        <BasicCheckbox
                                            title="Copy Vendors"
                                            checked={duplicateFormData.copyVendors}
                                            onChange={() => setDuplicateFormData((prev) => ({ ...prev, copyVendors: !prev.copyVendors }))}
                                        />
                                    </div>
                                </div>
                                <div className={cx('form-actions')}>
                                    <BasicButton
                                        title={isDuplicating ? 'Duplicating...' : 'Duplicate Workspace'}
                                        onClick={handleDuplicate}
                                        isProcessing={isDuplicating}
                                    />
                                    <BasicButton
                                        title="Cancel"
                                        onClick={() => setDuplicatingWorkspace(null)}
                                    />
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default WorkspaceManagementTab; 